import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import {
  loginToApiCore,
  hasValidSession,
  clearSession,
  saveSession,
  getSessionData,
  getPsychologistId as getStoredPsychologistId,
  type JWTUserData
} from '../hooks/api';
import apiCoreAdapter from '../hooks/apiCoreAdapter';
import { syncOnboardingFromAPI, resetOnboardingSyncGate } from '../utils/onboarding';

interface PsychologistUser {
  email: string;
  name: string;
  surname: string;
  psychologistId: string | null;
}

interface AuthContextType {
  user: PsychologistUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  signOut: () => void;
  authenticateWithApiCore: (email: string, googleAccessToken: string) => Promise<boolean>;
  psychologistId: string | null;
  clearError: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<PsychologistUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [psychologistId, setPsychologistId] = useState<string | null>(null);

  useEffect(() => {
    // Cargar sesión desde sessionStorage (fuente de verdad)
    const sessionData = getSessionData();
    if (!sessionData) return;

    const name = sessionData.user.name;
    const surname = sessionData.user.surname || '';

    setUser({
      email: sessionData.user.email,
      name,
      surname,
      psychologistId: sessionData.psychologist_id
    });
    setPsychologistId(sessionData.psychologist_id);

    // Sincronizar onboarding desde la API (siempre, en session restore)
    syncOnboardingFromAPI();

    // Si el nombre está vacío (ej: impersonación), obtenerlo del perfil
    if (!name.trim()) {
      apiCoreAdapter.psychologistProfile().then(profile => {
        const profileName = profile.name || '';
        const profileSurname = profile.surname || '';
        if (!profileName && !profileSurname) return;

        // `name` guarda solo el nombre de pila en todo el resto de la app (ver
        // MainLayout, PsiPlusChatbot, usePatientsData, etc. — todos concatenan
        // `${user.name} ${user.surname}`). Guardar acá el nombre completo
        // duplicaba el apellido en cualquier lugar que hiciera esa concatenación.
        setUser(prev => prev ? { ...prev, name: profileName, surname: profileSurname } : prev);

        // Actualizar sesión persistida para que no vuelva a hacer el fetch
        const current = getSessionData();
        if (current) {
          saveSession(current.token, { ...current.user, name: profileName, surname: profileSurname }, current.scopes);
        }
      }).catch(() => { /* noop */ });
    }
  }, []);

  const clearSessionData = useCallback(() => {
    // Limpiar el estado actual
    setUser(null);
    setPsychologistId(null);
    
    // Limpiar toda la información anterior del cache y contexto
    try {
      // Limpiar sessionStorage
      sessionStorage.removeItem('psi-auth-token');
      sessionStorage.removeItem('psi-session-data');
      
      // Limpiar localStorage - incluyendo items de Google Auth
      localStorage.removeItem('psi-connect-user');
      localStorage.removeItem('psi-connect-psychologist');
      localStorage.removeItem('psi-timezone');
      localStorage.removeItem('google_user');
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('oauth_state');
      
      // Limpiar cualquier otro item relacionado con autenticación
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('psi-') || key.startsWith('google-') || key.startsWith('firebase:'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (err) {
      console.warn('⚠️ Error al limpiar cache anterior:', err);
    }
  }, []);

  const authenticateWithApiCore = useCallback(async (email: string, googleAccessToken: string): Promise<boolean> => {
    if (isLoading) {
      return false;
    }

    // Primero limpiar toda la información anterior
    clearSessionData();

    setIsLoading(true);
    setError(null);

    try {
      const response = await loginToApiCore(email, googleAccessToken);
      
      if (response.success && response.data.token) {
        // SOLO usar información de la respuesta de API Core
        const userData: JWTUserData = {
          id: response.data.user.id,
          email: response.data.user.email,
          name: response.data.user.name,
          surname: response.data.user.surname || ''
        };

        const saved = saveSession(
          response.data.token, 
          userData, 
          response.data.scopes
        );

        if (!saved) {
          throw new Error('Error al guardar la sesión');
        }
        
        const psychId = getStoredPsychologistId();
        setPsychologistId(psychId);
        
        // Establecer usuario SOLO con datos de API Core
        const newUser: PsychologistUser = {
          email: userData.email,
          name: userData.name,
          surname: userData.surname || '',
          psychologistId: psychId
        };
        
        setUser(newUser);

        resetOnboardingSyncGate();
        await syncOnboardingFromAPI();

        setIsLoading(false);
        return true;
      } else {
        setError('Respuesta inválida del servidor');
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error de autenticación';
      setError(errorMessage);
      setIsLoading(false);
      clearSession();
      throw err;
    }
  }, [isLoading, clearSessionData]);

  const signOut = useCallback(() => {
    try {
      clearSession();
      setUser(null);
      setPsychologistId(null);
      setError(null);
    } catch (err) {
      setUser(null);
      setPsychologistId(null);
      setError(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isAuthenticated = hasValidSession() && user !== null;

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    error,
    signOut,
    authenticateWithApiCore,
    psychologistId,
    clearError
  }), [user, isAuthenticated, isLoading, error, signOut, authenticateWithApiCore, psychologistId, clearError]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};